/**
 * URL-hash state serialization for the wind-drift visualizer.
 *
 * Pattern:
 *   /wind-drift.html#s=<base64url-encoded JSON>
 *
 * The encoded payload is { v: 1, a: BuildA, b: BuildB|null, e: Environment, r: rangeYards }.
 * Round-trippable. Used both for share URLs and for browser-restore on reload.
 */

import type { Build, Environment } from '../lib/trajectory/types';

export interface SerializedState {
  v: 1;
  a: Build;
  b: Build | null;
  e: Environment;
  r: number;
}

/** Clamp a value into [min, max], falling back to `fallback` if non-finite. */
function clampNum(v: unknown, min: number, max: number, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/**
 * Coerce a decoded build into physically valid ranges. Shared URLs (and old
 * links) can carry non-physical values — most dangerously arrowGrains = 0,
 * which divides by zero in the solver and produces a NaN trajectory that
 * crashes the 3D scene. Bounds mirror the BuildPicker input limits.
 */
function sanitizeBuild(b: Build): Build {
  return {
    ...b,
    arrowGrains: clampNum(b.arrowGrains, 300, 800, 460),
    arrowLengthIn: clampNum(b.arrowLengthIn, 20, 34, 28.5),
    spine: clampNum(b.spine, 150, 700, 300),
    fOCPercent: clampNum(b.fOCPercent, 5, 25, 13),
    pointGrains: clampNum(b.pointGrains, 75, 300, 100),
    insertGrains: clampNum(b.insertGrains, 0, 150, 50),
    vaneOffsetDeg: clampNum(b.vaneOffsetDeg, 0, 6, 2),
    vaneCount: clampNum(b.vaneCount, 2, 4, 3),
    bowIboFps: clampNum(b.bowIboFps, 280, 360, 342),
    bowDrawWeightLb: clampNum(b.bowDrawWeightLb, 40, 90, 70),
    bowDrawLengthIn: clampNum(b.bowDrawLengthIn, 24, 32, 29),
    tuneQualityYawDeg: clampNum(b.tuneQualityYawDeg, 0, 15, 2),
    measuredVelocityFps:
      b.measuredVelocityFps == null
        ? undefined
        : clampNum(b.measuredVelocityFps, 200, 400, 280),
  };
}

export function encodeStateToHash(state: SerializedState): string {
  try {
    const json = JSON.stringify(state);
    const b64 = btoa(json)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    return `#s=${b64}`;
  } catch {
    return '';
  }
}

export function decodeStateFromHash(hash: string): SerializedState | null {
  try {
    if (!hash.startsWith('#s=')) return null;
    const b64 = hash.slice(3).replace(/-/g, '+').replace(/_/g, '/');
    /* Repad */
    const pad = b64.length % 4;
    const padded = pad ? b64 + '='.repeat(4 - pad) : b64;
    const json = atob(padded);
    const parsed = JSON.parse(json);
    if (parsed && parsed.v === 1) {
      const state = parsed as SerializedState;
      return {
        ...state,
        a: sanitizeBuild(state.a),
        b: state.b ? sanitizeBuild(state.b) : null,
        r: clampNum(state.r, 10, 120, 60),
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function copyShareUrl(state: SerializedState): Promise<void> {
  const hash = encodeStateToHash(state);
  const url = window.location.origin + window.location.pathname + hash;
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(url);
  }
  /* Fallback */
  const ta = document.createElement('textarea');
  ta.value = url;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
  return Promise.resolve();
}
